import type { BreadcrumbLink } from '@evisort/eds/components/Breadcrumb/types';

import { TopNav } from '@evisort/eds/components/AppLayout/subcomponents/TopNav/TopNav';
import { Breadcrumb } from '@evisort/eds/components/Breadcrumb/Breadcrumb';
import { type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type {
  BreadcrumbResolver,
  BreadcrumbResolverResult,
} from '~/routing/types';

import { useSidebarNav } from '~/features/app/SidebarNavContext';

import { useAppBreadcrumbSlot } from './AppBreadcrumbSlotContext';
import { AppHeaderActions } from './AppHeaderActions';
import { useAppNavBrand } from './useAppNavBrand';
import {
  type BreadcrumbItem,
  useRouteBreadcrumbs,
} from './useRouteBreadcrumbs';

export const APP_BREADCRUMB_HEIGHT = 48;

const messages = defineMessages({
  showSidebar: {
    id: 'AppBreadcrumb.showSidebar',
    defaultMessage: 'Show sidebar',
    description:
      'Tooltip and a11y label for the mobile sidebar toggle when the sidebar is collapsed.',
  },
  loadingCrumb: {
    id: 'AppBreadcrumb.loadingCrumb',
    defaultMessage: 'Loading',
    description:
      'Accessible label for a breadcrumb whose name is still loading.',
  },
});

// Until a dynamic crumb's label resolves, hand EDS a loading crumb
// (`isLoading`) so it renders its own shimmer placeholder.
function toBreadcrumbLink(
  crumb: BreadcrumbItem,
  result: BreadcrumbResolverResult | undefined,
): BreadcrumbLink {
  const path = crumb.pathname ?? '';
  if (crumb.kind === 'static') {
    return { path, text: crumb.text };
  }
  return !result || result.isLoading || result.label === null
    ? { path, isLoading: true }
    : { path, text: result.label };
}

export function AppBreadcrumb() {
  const intl = useIntl();
  const { isMobile, toggleCollapsed } = useSidebarNav();
  const brand = useAppNavBrand('breadcrumb-brand-button');

  return (
    <TopNav
      actions={<AppHeaderActions />}
      brand={brand}
      dataAttributes={{ 'data-amp-track-region': 'breadcrumbs' }}
      menuToggle={
        isMobile
          ? {
              label: intl.formatMessage(messages.showSidebar),
              onClick: toggleCollapsed,
            }
          : undefined
      }
    />
  );
}

/**
 * The route's breadcrumb trail, for the layout's `breadcrumbs` slot. The top
 * bar shows it between the brand and the header actions.
 */
export function AppBreadcrumbTrail() {
  const intl = useIntl();
  const trail = useRouteBreadcrumbs();
  const { setNode } = useAppBreadcrumbSlot();

  // A deferred route's page portals its own trail into the slot below, so this
  // renders the slot and nothing else. The crumbs are coming, just from the
  // page.
  const isDeferred = trail.mode === 'deferred';
  const crumbs = trail.mode === 'resolved' ? trail.crumbs : [];

  if (!isDeferred && crumbs.length === 0) {
    return null;
  }

  return (
    <div ref={isDeferred ? setNode : null}>
      {!isDeferred && (
        <BreadcrumbTrail
          crumbs={crumbs}
          loadingLabel={intl.formatMessage(messages.loadingCrumb)}
        />
      )}
    </div>
  );
}

// Resolves a single dynamic crumb's label with its resolver hook, then renders
// `children` with the result. Keyed by the caller so it remounts when the
// resolver changes, keeping its one hook call valid across navigations.
function DynamicCrumbResolver({
  resolver,
  children,
}: {
  resolver: BreadcrumbResolver;
  children: (result: BreadcrumbResolverResult) => ReactNode;
}) {
  const useResolver = resolver;
  return <>{children(useResolver())}</>;
}

interface BreadcrumbTrailProps {
  crumbs: BreadcrumbItem[];
  loadingLabel: string;
}

/**
 * A trail can carry any number of dynamic crumbs, each backed by its own
 * resolver hook. React forbids calling a route-dependent number of hooks in a
 * loop, so each dynamic crumb is resolved by its own `DynamicCrumbResolver`,
 * nested into a chain that threads results down to a single EDS `Breadcrumb`.
 * Labels resolve during render, so a crumb shows EDS's loading placeholder only
 * while its query is genuinely pending.
 */
function BreadcrumbTrail({ crumbs, loadingLabel }: BreadcrumbTrailProps) {
  const resolveFrom = (
    index: number,
    results: Record<number, BreadcrumbResolverResult>,
  ): ReactNode => {
    if (index >= crumbs.length) {
      return (
        <Breadcrumb
          links={crumbs.map((crumb, i) => toBreadcrumbLink(crumb, results[i]))}
          loadingLabel={loadingLabel}
        />
      );
    }

    const crumb = crumbs[index];
    if (crumb.kind !== 'dynamic') {
      return resolveFrom(index + 1, results);
    }

    return (
      <DynamicCrumbResolver
        key={`${index}:${crumb.resolver.name}`}
        resolver={crumb.resolver}
      >
        {(result) => resolveFrom(index + 1, { ...results, [index]: result })}
      </DynamicCrumbResolver>
    );
  };

  return <>{resolveFrom(0, {})}</>;
}
